import { Button } from '@merels/ui'
import { RefreshCw, User } from 'lucide-react'
import { Project, formatRelativeTime } from './types'

interface ProjectsListProps {
  projects: Project[]
  onRefresh: () => void
  onProjectClick: (projectId: string) => void
  onDeleteProject: (projectId: string) => void
}

export function ProjectsList({
  projects,
  onRefresh,
  onProjectClick,
  onDeleteProject,
}: ProjectsListProps) {
  return (
    <div className="space-y-4 flex flex-col flex-1">
      <div className="flex items-center justify-between shrink-0">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-100">Published Games and Creator Projects</h2>
        </div>
        <Button onClick={onRefresh} variant="secondary" className="h-7 w-7 p-0 flex items-center justify-center bg-[#141620] hover:bg-[#1f2231] border border-[#222535]">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-[#1e212f] bg-[#0c0d13] flex-1">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-[#1e212f] bg-[#12141c]/50 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <th className="p-3">Project / Slug</th>
              <th className="p-3">Publisher Owner</th>
              <th className="p-3">Last Published</th>
              <th className="p-3 text-right">Last Active</th>
              <th className="p-3 text-right w-16">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#181a25]">
            {projects.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-muted-foreground italic">
                  No published games or creator projects located yet.
                </td>
              </tr>
            ) : (
              projects.map((project) => {
                const hasFullSlug = project.publisherSlug && project.gameSlug
                const displaySlug = hasFullSlug
                  ? `${project.publisherSlug}/${project.gameSlug}`
                  : project.gameSlug
                    ? `.../${project.gameSlug}`
                    : null

                const displayText = displaySlug || project.projectId
                
                return (
                  <tr 
                    key={project.projectId}
                    onClick={() => onProjectClick(project.projectId)}
                    className="hover:bg-[#151722]/50 cursor-pointer transition-colors group"
                  >
                    <td className="p-3 whitespace-nowrap">
                      <span className="font-mono text-xs text-gray-200">
                        {displayText}
                      </span>
                      {!hasFullSlug && displaySlug && (
                        <span className="text-[10px] text-muted-foreground font-mono ml-2">
                          (ID: {project.projectId})
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center space-x-1.5 text-gray-300">
                        <User className="h-3 w-3 text-muted-foreground" />
                        <span>{project.ownerName || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="p-3 font-mono text-gray-300">
                      {project.lastPublished ? (
                        <span title={new Date(project.lastPublished).toLocaleString()} className="cursor-help">
                          {formatRelativeTime(project.lastPublished)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic text-[10px]">Never</span>
                      )}
                    </td>
                    <td className="p-3 text-right font-mono text-gray-300">
                      {project.lastActive ? (
                        <span title={new Date(project.lastActive).toLocaleString()} className="cursor-help">
                          {formatRelativeTime(project.lastActive)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic text-[10px]">Never</span>
                      )}
                    </td>
                    <td className="p-3 text-right font-mono text-gray-300" onClick={(e) => e.stopPropagation()}>
                      {project.telemetryProjectId && <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (confirm(`Are you sure you want to delete all telemetry records for project "${displayText}"?`)) {
                            void onDeleteProject(project.telemetryProjectId!)
                          }
                        }}
                        className="text-red-500 hover:text-red-400 text-[11px] font-bold underline transition-colors"
                      >
                        Delete
                      </button>}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
